// Browser-shell concerns kept out of game.ts: the rotate-gate predicate (pure,
// tested below) plus, added in a later task, the Fullscreen API / orientation
// lock / resize wiring. IMPORTANT: no DOM access at module top level — only
// inside function bodies — so this file is importable under `bun test`.

/** Show the "turn sideways" gate only on a touch device (coarse pointer) held
 *  in portrait — never on a narrow desktop window, which just letterboxes. */
export function shouldPromptRotate(isPortrait: boolean, isCoarse: boolean): boolean {
  return isPortrait && isCoarse;
}

// ── fullscreen (feature-detected; every call is best-effort) ─────────────────
type FsElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};

export function isFullscreenSupported(): boolean {
  const el = document.documentElement as FsElement;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen);
}

export function isFullscreen(): boolean {
  const d = document as FsDocument;
  return !!(document.fullscreenElement ?? d.webkitFullscreenElement);
}

function enterFullscreen(): Promise<void> {
  const el = document.documentElement as FsElement;
  try {
    if (el.requestFullscreen) {
      return Promise.resolve(el.requestFullscreen({ navigationUI: "hide" })).catch(() => {});
    }
    if (el.webkitRequestFullscreen) {
      return Promise.resolve(el.webkitRequestFullscreen()).catch(() => {});
    }
  } catch {
    /* ignore — unsupported */
  }
  return Promise.resolve();
}

function exitFullscreen(): void {
  const d = document as FsDocument;
  try {
    if (document.exitFullscreen) void document.exitFullscreen().catch(() => {});
    else if (d.webkitExitFullscreen) void Promise.resolve(d.webkitExitFullscreen()).catch(() => {});
  } catch {
    /* ignore */
  }
}

function lockLandscape(): void {
  const orient = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> };
  try {
    void orient?.lock?.("landscape").catch(() => {});
  } catch {
    /* unsupported (iOS) — ignore */
  }
}

/** Go chrome-free + landscape on a user gesture (the Start tap / HUD toggle). */
export function requestImmersive(): void {
  void enterFullscreen().then(() => lockLandscape());
}

/** Toggle fullscreen from the HUD control. */
export function toggleFullscreen(): void {
  if (isFullscreen()) exitFullscreen();
  else requestImmersive();
}

// ── orientation gate + resize wiring ────────────────────────────────────────
const fsListeners: Array<() => void> = [];

/** Register a callback fired whenever fullscreen state changes (HUD label). */
export function onFullscreenChange(cb: () => void): void {
  fsListeners.push(cb);
}

function updateRotateGate(): void {
  const el = document.getElementById("rotate");
  if (!el) return;
  const portrait = window.matchMedia("(orientation: portrait)").matches;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  el.hidden = !shouldPromptRotate(portrait, coarse);
}

/** Wire viewport listeners once at boot. `onResize` re-fits the game canvas
 *  (this is the fix for ungraceful portrait→landscape: iOS fires `resize`
 *  unreliably on rotation, so we also listen to orientationchange + visualViewport). */
export function initViewport(onResize?: () => void): void {
  const refit = () => {
    updateRotateGate();
    onResize?.();
  };
  // After fullscreen / orientation transitions the viewport dimensions settle a
  // frame (or more) late, so re-fit now, on the next frame, and once more shortly
  // after — otherwise the canvas keeps a stale (often shrunken) size.
  const refitSoon = () => {
    refit();
    requestAnimationFrame(refit);
    setTimeout(refit, 300);
  };
  const fsChanged = () => {
    for (const cb of fsListeners) cb();
    refitSoon(); // entering/leaving fullscreen MUST re-fit the canvas
  };
  refit();
  window.addEventListener("resize", refit);
  window.addEventListener("orientationchange", refitSoon);
  // visualViewport fires on URL-bar show/hide (resize) and pan (scroll); both
  // change the visible area we size to.
  window.visualViewport?.addEventListener("resize", refit);
  window.visualViewport?.addEventListener("scroll", refit);
  document.addEventListener("fullscreenchange", fsChanged);
  document.addEventListener("webkitfullscreenchange", fsChanged as EventListener);
}
